import { z } from 'zod';
import fetch from '../../../js/api/fetch';
import { addError } from '../Store/MetaData/action';
import { setAllCartItemData } from '../Store/CartItem/action';
import { setAllPricesData } from '../Store/Prices/action';
import { setAllUpsellData } from '../Store/Upsell/action';
import type { TypedDispatch } from '../Store/store';
import { setUIState } from '../Store/UI/actions';

const PriceSchema = z.object({
    currency: z.string(),
    value: z.number(),
});

const CartItemSchema = z.object({
    amount: z.number(),
    amountMutable: z.boolean(),
    copyPersonalisationUrl: z.string().nullable(),
    displayAmount: z.string(),
    editUrl: z.string(),
    giftCard: z.object({
        active: z.boolean(),
        maxLength: z.number(),
        message: z.string(),
    }),
    id: z.string(),
    isAlcohol: z.boolean(),
    isDiscounted: z.boolean(),
    isStrongAlcohol: z.boolean(),
    name: z.string(),
    originalPrice: PriceSchema,
    previewImage: z.string(),
    price: PriceSchema,
    productId: z.number(),
    productUrl: z.string(),
    shippingText: z.string(),
});

const UpsellCategorySchema = z.object({
    id: z.number(),
    priority: z.number(),
});

const UpsellProductSchema = z.object({
    amountInCart: z.number(),
    amountMutable: z.boolean(),
    cartItemId: z.number(),
    editHash: z.string().nullable(),
    extraInformation: z.string(),
    id: z.number(),
    imgUrl: z.string(),
    inCart: z.boolean(),
    isHappyArProduct: z.boolean(),
    locked: z.boolean(),
    name: z.string(),
    personalized: z.boolean(),
    previewImage: z.string().nullable(),
    price: z.object({
        numericValue: z.number(),
        value: z.string(),
    }),
    uniqueId: z.string().nullable(),
});

const RootStateSchema = z.object({
    cartItems: z.object({
        cartContainsAlcohol: z.boolean(),
        cartContainsStrongAlcohol: z.boolean(),
        items: z.record(CartItemSchema),
    }),
    prices: z.object({
        discount: PriceSchema,
        discountCode: z.string(),
        hasDiscount: z.boolean(),
        isDiscountCodeValid: z.boolean(),
        shippingPrice: PriceSchema,
        subTotal: PriceSchema,
        total: PriceSchema,
        voucher: PriceSchema,
        voucherCode: z.string(),
    }),
    ui: z.object({
        isAllowedToProceedToCheckout: z.boolean(),
        userHasSelectedShippingMethod: z.boolean(),
    }),
    upsells: z.object({
        editorFontCacheVersion: z.string().nullable(),
        groups: z.record(z.array(z.object({
            category: UpsellCategorySchema,
            id: z.number(),
            products: z.array(UpsellProductSchema),
        }))),
    }),
});

export const updateRootState = async (dispatch: TypedDispatch): Promise<void> => {
    try {
        const response = await fetch('checkout/shopping-cart/data', {
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
            },
            method: 'GET',
        });

        const rootState = RootStateSchema.safeParse(await response.json());

        if (rootState.success) {
            dispatch(setAllCartItemData(rootState.data.cartItems));
            dispatch(setAllPricesData(rootState.data.prices));
            dispatch(setAllUpsellData(rootState.data.upsells));
            dispatch(setUIState(rootState.data.ui));
        }
    } catch (e: unknown) {
        dispatch(addError());
    }
};
